import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useAtomValue } from "jotai";
import { CREATE_STRIPE_CONNECTED_ACCOUNT } from "../../../apis/lambda/GraphQL/Stripe/mutations";
import { GET_USER } from "../../../apis/lambda/GraphQL/User/queries";
import { userInfoAtom } from "../../../atoms";
import { useUserBalance } from "../../../hooks/useUserBalance";
import { useTransferBalance } from "./useTransferBalance";

interface TransferModalProps {
  opened: boolean;
  onClose: () => void;
}

function TransferModal({ opened, onClose }: TransferModalProps) {
  // Jotai State
  const userInfo = useAtomValue(userInfoAtom);

  // GraphQL
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery(GET_USER, {
    variables: {
      email: userInfo?.email!,
    },
    skip: !userInfo?.email || !opened,
  });
  const {
    balance,
    loading: balanceLoading,
    error: balanceError,
  } = useUserBalance();
  // Mutations
  const {
    transfer,
    loading: transferLoading,
    error: transferError,
  } = useTransferBalance();
  const [
    createStripeConnectedAccount,
    { loading: stripeLoading, error: stripeError },
  ] = useMutation(CREATE_STRIPE_CONNECTED_ACCOUNT);

  const handleCreateStripeConnectedAccount = async () => {
    try {
      const response = await createStripeConnectedAccount();
      const url = response.data?.createStripeConnectedAccount;
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Error creating Stripe connected account:", error);
    }
  };

  const handleTransfer = async () => {
    await transfer();
    onClose();
  };

  const loading = transferLoading || stripeLoading || userLoading;
  const hasError = userError || balanceError || transferError || stripeError;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={3}>Transfer Balance</Title>}
      size="md"
      centered
    >
      {/* Show loading state while fetching user data or balance */}
      {(userLoading || balanceLoading) && (
        <Stack gap="md" align="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Loading transfer information...</Text>
        </Stack>
      )}

      {/* Show error state if any query/mutation failed */}
      {!userLoading && !balanceLoading && hasError && (
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error Loading Transfer Information"
            color="red"
          >
            {userError?.message ||
              balanceError?.message ||
              transferError?.message ||
              stripeError?.message ||
              "An unexpected error occurred. Please try again."}
          </Alert>
          <Group justify="flex-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      )}

      {/* Show content when data is loaded and no errors */}
      {!userLoading && !balanceLoading && !hasError && userData?.user && (
        <>
          {userData.user.payoutsEnabled ? (
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                You are about to transfer{" "}
                <Text span fw={700} c="green">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(balance)}
                </Text>{" "}
                to your connected bank account.
              </Text>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={700}>Available Balance:</Text>
                  <Text fw={700} c="green">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(balance)}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" ta="right">
                  (Platform fee of 1% already deducted)
                </Text>
              </Stack>

              <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleTransfer}
                  disabled={balance === 0 || loading}
                  loading={transferLoading}
                >
                  Transfer{" "}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(balance)}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack gap="md">
              <Text>
                To transfer your balance, you need to connect a Stripe account.
              </Text>
              <Text>
                This allows us to securely transfer your earnings to your bank
                account.
              </Text>
              <Group justify="flex-end">
                <Button variant="outline" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateStripeConnectedAccount}
                  loading={stripeLoading}
                  disabled={stripeLoading}
                >
                  Connect with Stripe
                </Button>
              </Group>
            </Stack>
          )}
        </>
      )}
    </Modal>
  );
}

export default TransferModal;
